/**
 * Profile Service
 * Handles all Firebase operations for public user profiles.
 */

import { getDatabase, ref, get, set, update } from 'firebase/database';

/**
 * Fetch a public profile by username.
 * @param {string} username
 * @returns {Promise<{success: boolean, profile?: object, error?: string}>}
 */
export async function getPublicProfile(username) {
  if (!username) return { success: false, error: 'No username provided' };
  const db = getDatabase();
  const snap = await get(ref(db, `publicProfiles/${username}`));
  if (!snap.exists()) return { success: false, error: 'Profile not found' };
  return { success: true, profile: snap.val() };
}

/**
 * Save (create or update) a public profile.
 * Firebase rules enforce that only the owner can write to their own profile path.
 *
 * @param {string} username
 * @param {{ description?: string, tiktok?: string, instagram?: string, avatar?: number }} data
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function savePublicProfile(username, data) {
  if (!username) return { success: false, error: 'No username provided' };

  const db = getDatabase();
  const profile = {
    username,
    updatedAt: new Date().toISOString(),
  };

  if (data.description !== undefined) profile.description = data.description;
  if (data.tiktok !== undefined) profile.tiktok = data.tiktok;
  if (data.instagram !== undefined) profile.instagram = data.instagram;
  if (data.avatar !== undefined) profile.avatar = data.avatar;

  try {
    await set(ref(db, `publicProfiles/${username}`), profile);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Mark the user's profile as created in their private user record.
 * This hides the profile creation banner permanently.
 * @param {string} uid
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function markProfileCreated(uid) {
  if (!uid) return { success: false, error: 'No uid provided' };
  const db = getDatabase();
  try {
    await update(ref(db, `users/${uid}`), { profileCreated: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Delete a user's public profile entirely and reset their profileCreated flag.
 * @param {string} username
 * @param {string} uid
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deletePublicProfile(username, uid) {
  if (!username || !uid) return { success: false, error: 'Invalid arguments' };
  const db = getDatabase();
  try {
    await update(ref(db, '/'), {
      [`publicProfiles/${username}`]: null,
      [`users/${uid}/profileCreated`]: null,
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Mark that the user has seen/acknowledged the Expand Collection feature.
 * @param {string} uid
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function markExpandCollectionSeen(uid) {
  if (!uid) return { success: false, error: 'No uid provided' };
  const db = getDatabase();
  try {
    await update(ref(db, `users/${uid}`), { expandCollectionSeen: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Delete optional fields from a profile (set them to null to remove from Firebase).
 * Used when a user clears a social handle or description.
 * @param {string} username
 * @param {string[]} fields - field names to delete e.g. ['tiktok', 'description']
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function clearProfileFields(username, fields) {
  if (!username || !fields?.length) return { success: false, error: 'Invalid arguments' };
  const db = getDatabase();
  const updates = {};
  for (const field of fields) {
    updates[`publicProfiles/${username}/${field}`] = null;
  }
  updates[`publicProfiles/${username}/updatedAt`] = new Date().toISOString();
  try {
    await update(ref(db, '/'), updates);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}
